// Traditional Chinese (Hong Kong) translations. Register: formal 書面語
// in 繁體字, appropriate for institutional biotech / investor communication.

export const LANGS = ["en", "zh", "zhCn"];
export const LANG_LABEL = { en: "EN", zh: "繁", zhCn: "简" };
export const LANG_HTML = { en: "en", zh: "zh-Hant-HK", zhCn: "zh-Hans" };

const en = {
  nav: {
    framework: "Framework",
    process: "Process",
    advisory: "Advisory",
    insights: "Insights",
    register: "Express Interest",
    infrastructure: "Infrastructure",
    partners: "Partners",
    requestInfo: "Request Information",
    toggleAria: "Switch language",
  },
  hero: {
    line1: "Biological Assurance",
    line2Italic: "Through",
    line3: "Autologous Blood Banking.",
    subheadline:
      "Headquartered in Hong Kong, MiCells® is developing infrastructure for long-term personal blood banking, enabling individuals to preserve, monitor and maintain access to their own blood for future medical use.",
    primaryCta: "Express Interest",
    secondaryCta: "Our Framework",
  },
  pillars: {
    eyebrow: "01 · Pillars",
    headingA: "Four principles behind",
    headingB: "long-term personal blood banking.",
    items: [
      {
        title: "Medical Autonomy",
        body:
          "Your blood. Your biology. Preserved under autologous protocols.",
      },
      {
        title: "Clinical Integrity",
        body:
          "Developed alongside senior medical professionals and governed by rigorous, independently reviewed standards.",
      },
      {
        title: "Security & Traceability",
        body:
          "Comprehensive chain-of-custody and asset verification throughout the entire preservation lifecycle.",
      },
      {
        title: "Global Readiness",
        body:
          "Designed to support international mobility and emergency responsiveness across jurisdictions.",
      },
    ],
  },
  clients: {
    eyebrow: "02 · Clients",
    headingA: "Designed for individuals planning",
    headingB: "beyond conventional healthcare.",
    body:
      "MiCells® supports individuals and families seeking long-term biological preparedness through clinically governed personal blood banking.",
    list: [
      "Individuals Seeking Long-Term Preparedness",
      "High-Net-Worth Individuals",
      "Individuals With Rare Blood Types",
      "Family Offices & Legacy Planning",
      "International Healthcare Clients",
    ],
  },
  process: {
    eyebrow: "03 · Process",
    headingA: "Five stages of",
    headingB: "custody.",
    steps: [
      {
        title: "Medical Consultation",
        body:
          "Initial clinical assessment to determine suitability, individual considerations and a tailored preservation plan.",
      },
      {
        title: "Collection & Verification",
        body:
          "Controlled biological collection by accredited clinical partners, followed by identity, integrity and provenance verification.",
      },
      {
        title: "Secure Preservation",
        body:
          "Transfer into institutional preservation infrastructure under advanced cryogenic protocols and continuous monitoring.",
      },
      {
        title: "Ongoing Stewardship",
        body:
          "Lifecycle integrity management, periodic review, redundant storage architecture and confidential client reporting.",
      },
      {
        title: "Mobilisation Upon Need",
        body:
          "Coordinated release to the treating clinical institution with complete documented chain-of-custody.",
      },
    ],
  },
  framework: {
    eyebrow: "04 · Framework",
    headingA: "Built upon",
    headingB: "clinical oversight.",
    body:
      "The MiCells® framework is designed to be defensible to clinicians, auditors and regulators alike.",
    items: [
      {
        title: "Medical Advisory Board",
        body:
          "Senior medical professionals advising on clinical safety, suitability protocols and the evolving science of preservation.",
      },
      {
        title: "Compliance Philosophy",
        body:
          "A pro-active regulatory posture that anticipates institutional, cross-border and emerging compliance frameworks.",
      },
      {
        title: "Clinical Governance",
        body:
          "Independent governance over decisions impacting biological integrity, client safety and chain-of-custody.",
      },
      {
        title: "Quality Assurance",
        body:
          "Documented quality protocols across collection, transport, storage and mobilisation. Auditable end to end.",
      },
    ],
  },
  advisory: {
    eyebrow: "05 · Advisory",
    headingA: "Advisory",
    headingB: "Board.",
    ctaLabel: "Senior medical or scientific advisor?",
    ctaHeading: "We welcome confidential advisory discussions.",
    ctaButton: "Discuss an Advisory Seat",
  },
  insights: {
    eyebrow: "06 · Insights",
    headingA: "Research &",
    headingB: "perspectives.",
    read: "Read Perspective",
  },
  register: {
    eyebrow: "07 · Express Interest",
    headingA: "Express your",
    headingB: "interest in MiCells®.",
    intro:
      "A confidential, non-binding channel for prospective clients of the MiCells® autologous blood banking programme. Your submission helps us plan capacity, refine our service design and prioritise engagement across Hong Kong and international markets.",
    assurances: [
      {
        title: "Non-binding",
        body: "No contract is created and no obligation arises on either side.",
      },
      {
        title: "Held in confidence",
        body: "Never sold, never shared with third parties. Withdrawable on request.",
      },
      {
        title: "Forecasting purpose",
        body: "Used only to model prospective client numbers and refine the offering.",
      },
    ],
    groups: {
      about: "01 · About You",
      profile: "02 · Personal Profile",
      interest: "03 · Interest & Timing",
      optional: "04 · Optional",
    },
    fields: {
      name: "Full Name",
      email: "Email",
      phone: "Phone (with country code)",
      phoneHint: "Optional",
      country: "Country of Residence",
      age: "Age Band",
      bloodType: "Blood Type",
      household: "Household Coverage",
      householdCount: "Individuals",
      motivation: "Primary Motivation",
      timeline: "Preferred Engagement Timeline",
      tier: "Preferred Service Tier",
      referral: "How you heard about us",
      referralHint: "Optional",
      notes: "Additional context",
      notesPlaceholder: "Anything else you would like us to know.",
    },
    options: {
      age: [
        { v: "18-30", l: "18 – 30" },
        { v: "31-45", l: "31 – 45" },
        { v: "46-60", l: "46 – 60" },
        { v: "61-75", l: "61 – 75" },
        { v: "76+", l: "76 +" },
        { v: "prefer_not", l: "Prefer not to say" },
      ],
      bloodType: [
        { v: "unknown", l: "Unknown" },
        { v: "A+", l: "A+" },
        { v: "A-", l: "A−" },
        { v: "B+", l: "B+" },
        { v: "B-", l: "B−" },
        { v: "AB+", l: "AB+" },
        { v: "AB-", l: "AB−" },
        { v: "O+", l: "O+" },
        { v: "O-", l: "O−" },
        { v: "rare", l: "Rare / other" },
      ],
      household: [
        { v: "self", l: "Self only" },
        { v: "self_partner", l: "Self and partner" },
        { v: "family", l: "Whole family" },
        { v: "extended", l: "Extended family / office" },
      ],
      motivation: [
        { v: "preparedness", l: "Personal medical preparedness" },
        { v: "rare_blood", l: "Rare blood type" },
        { v: "family_history", l: "Family medical history" },
        { v: "longevity", l: "Longevity planning" },
        { v: "executive", l: "Executive or high-mobility lifestyle" },
        { v: "other", l: "Other" },
      ],
      timeline: [
        { v: "immediate", l: "Immediate (0 – 3 months)" },
        { v: "3-6", l: "3 – 6 months" },
        { v: "6-12", l: "6 – 12 months" },
        { v: "exploratory", l: "Exploratory only" },
      ],
      tier: [
        { v: "standard", l: "Standard" },
        { v: "priority", l: "Priority" },
        { v: "family", l: "Family" },
        { v: "undecided", l: "Undecided" },
      ],
    },
    terms: {
      eyebrow: "Non-Binding Terms",
      body:
        "By submitting, you acknowledge that this expression of interest is not a contract and creates no legal or financial obligation between you and MiCells®. Information provided will be used solely to forecast prospective client numbers, refine service design and inform capacity planning. Submissions are treated in confidence, never sold or shared with third parties, and may be withdrawn at any time by writing to info@micells.io.",
      consent:
        "I understand this is a non-binding expression of interest and consent to MiCells® holding this information for demand-forecasting and service-design purposes.",
    },
    submit: "Submit Expression of Interest",
    submitting: "Submitting…",
    validationRequired: "Please complete name and email to continue.",
    validationConsent: "Please tick the consent box to submit.",
    successTitle: "Thank you.",
    successBody:
      "Your expression of interest has been received. It will be treated in confidence and used only for demand-forecasting and service-design purposes. A member of the MiCells® team will be in touch from",
    successBodyTail: " should further information become relevant.",
    reset: "Submit another expression of interest",
    share: {
      eyebrow: "Refer a peer",
      body:
        "Know someone who would value private medical stewardship? A quiet forward means more than any public post.",
      copyLink: "Copy Link",
      copyLinkOk: "Link copied to clipboard",
      email: "Email",
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      emailSubject: "MiCells® — private medical stewardship",
      emailBody:
        "Thought this might be of interest — a clinical, institutional approach to personal medical preparedness.\n\nhttps://micells.io",
      shareText:
        "Thought this might be of interest — a clinical, institutional approach to personal medical preparedness. https://micells.io",
      followLinkedin: "Follow MiCells® on LinkedIn",
      close: "Close",
    },
    toastSuccess: "Expression of interest received.",
    toastFail:
      "Unable to submit. Please try again or email info@micells.io directly.",
    captcha: {
      eyebrow: "Human Verification",
      headingA: "Sequence the",
      headingB: "MiCells® process.",
      body:
        "Tap each step into the correct order it occurs in the MiCells® programme. This confirms you're human before we submit your expression of interest.",
      steps: [
        "Consultation",
        "Collection",
        "Preservation",
        "Stewardship",
      ],
      steps_label: "Steps",
      yourOrder: "Your Order",
      emptySlot: "—",
      confirm: "Confirm Sequence",
      reset: "Reset",
      cancel: "Cancel",
      wrong: "Order not correct — please try again.",
      attempts: "Attempt {n}",
    },
  },
  investors: {
    eyebrow: "08 · Partners",
    headingA: "Partners &",
    headingB: "Investors.",
    body:
      "MiCells® is a Hong Kong-based healthcare infrastructure company operating at the intersection of preservation and long-term medical continuity. Engagement is by introduction and qualified enquiry.",
    tracks: [
      {
        label: "Strategic Partnerships",
        body:
          "Clinical, preservation, logistics and healthcare partners supporting the build-out of long-term personal blood banking infrastructure.",
      },
      {
        label: "Medical & Strategic Advisory",
        body:
          "Supporting clinical governance, regulatory development and long-term healthcare deployment.",
      },
      {
        label: "Investor Enquiries",
        body:
          "Qualified investor enquiries from family offices, institutional funds and aligned long-horizon capital.",
      },
    ],
    materialsLabel: "Investor Materials",
    materialsBody:
      "Investor information is available on request to qualified parties.",
    cta: "Request Investor Information",
    trackPrefix: "Track",
  },
  infrastructureTeaser: {
    eyebrow: "09 · Infrastructure",
    headingA: "Public infrastructure for the",
    headingB: "Midnight Network.",
    body:
      "MiCells® operates a high-availability public relay node in Singapore, strengthening APAC propagation and network resilience for privacy-preserving compute. Live status, peer identity and connection parameters are published for transparency.",
    stats: {
      status: "Node Status",
      statusValue: "Monitored",
      region: "Region",
      regionValue: "Singapore · APAC",
      network: "Network",
      networkValue: "Midnight · Mainnet Relay",
    },
    cta: "Explore the node",
    ctaAria: "Open the MiCells Midnight relay infrastructure page",
  },
  contact: {
    eyebrow: "10 · Contact",
    headingA: "Speak with",
    headingB: "MiCells®.",
    intro:
      "Enquiries are handled in confidence. A member of the MiCells® team will respond from",
    directLabel: "Direct",
    locationLabel: "Location",
    locationValue: "Hong Kong SAR",
    fields: {
      name: "Name",
      organisation: "Organisation",
      email: "Email",
      country: "Country",
      enquiryType: "Enquiry Type",
      message: "Message",
      messagePlaceholder: "Briefly describe the nature of your enquiry.",
    },
    types: {
      general: "General Enquiry",
      investor: "Investor Information",
      information: "Service Information",
      advisory: "Advisory / Partnership",
    },
    disclaimer:
      "By submitting, you consent to MiCells® contacting you in relation to this enquiry. We do not share enquirer details with third parties.",
    submit: "Send Enquiry",
    submitting: "Submitting…",
    successTitle: "Enquiry received.",
    successBody:
      "Thank you for contacting MiCells®. A member of our team will respond from",
    successBodyTail:
      ". If your enquiry is time-sensitive, please email us directly.",
    reset: "Submit another enquiry",
    validationRequired: "Please complete name, email and message.",
    toastSuccess:
      "Enquiry received. Our team will respond from info@micells.io.",
    toastFail:
      "Unable to submit enquiry. Please try again or email info@micells.io directly.",
  },
  footer: {
    description:
      "Medical infrastructure enabling individuals to preserve and access their own blood when required.",
    address: [
      "12F / Room 14A, Fonda Building",
      "37–39 Au Pui Wan Street",
      "Fo Tan, Sha Tin, New Territories",
    ],
    location: "Hong Kong SAR · Asia",
    contact: "Contact",
    connect: "Connect",
    legal: "Legal",
    linkedin: "LinkedIn",
    x: "X / Twitter",
    infrastructure: "Infrastructure",
    midnightRelay: "Midnight Relay",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    copyright: "© {year} MiCells®. All rights reserved.",
  },
  article: {
    close: "Close",
    published:
      "Published by MiCells®. For enquiries on any of the themes discussed, please contact us.",
    cta: "Speak with MiCells®",
    minutesSuffix: "min",
  },
  relay: {
    badge_syncing: "Node Online · Syncing",
    badge_synced: "Node Online · Fully Synced",
    badge_last_verified: "Verified Operator Snapshot",
    eyebrow: "Infrastructure · Public Node",
    hero_title_a: "Powering privacy-preserving",
    hero_title_b: "infrastructure",
    hero_title_c: "for Midnight.",
    hero_desc:
      "MiCells® operates a high-availability, high-throughput public relay node in Singapore supporting the Midnight Network ecosystem. Peer with us to strengthen APAC propagation and network resilience.",
    peer_count_label: "Active Peers",
    peer_count_suffix: "connected",
    sync_progress_label: "Sync Progress",
    uptime_label: "Node Status",
    uptime_value: "Monitored",
    primary_multiaddr_label: "Primary Multiaddr · IPv4",
    ws_tcp: "WS · TCP",
    peer_id_label: "Peer ID",
    copy_multiaddr: "Click to Copy Multiaddr",
    copy_multiaddr_short: "Copy Multiaddr",
    copy_peer_id: "Copy Peer ID",
    copy_short: "Copy",
    copied: "Copied",
    copied_toast: "Copied to clipboard",
    copy_fail_toast: "Copy failed — please copy manually.",
    peer_with_us: "Peer with MiCells®",
    sec1_tag: "01 · Strategic Vision",
    sec1_title_a: "Why MiCells® operates",
    sec1_title_b: "this node.",
    sec1_desc:
      "A privacy-preserving compute layer is a prerequisite for institutional-grade healthcare, financial and identity workloads. MiCells® contributes public relay capacity as a long-term act of network stewardship.",
    card1_title: "Confidential Execution Layer",
    card1_desc:
      "MiCells® leverages Midnight as a zero-knowledge execution environment: cryptographically-verifiable computation without compromising data privacy or decentralisation.",
    card2_title: "ZK-SNARK Compliance & Protection",
    card2_desc:
      "Zero-knowledge proofs allow private computation with selective disclosure — the primitive that makes privacy-preserving infrastructure defensible to institutions, auditors and regulators.",
    card3_title: "Public Infrastructure Commitment",
    card3_desc:
      "MiCells® commits to maintaining free, low-latency public relay capacity in the APAC region, strengthening peer propagation and long-term network resiliency for the Midnight ecosystem.",
    sec2_tag: "02 · Hardware & Software",
    sec2_title_a: "Enterprise readiness",
    sec2_title_b: "by design.",
    sec2_desc:
      "Over-provisioned datacenter hardware and a hardened, audited software stack — the minimum baseline for infrastructure MiCells® is willing to run under its own name.",
    hw_title: "Server Specifications",
    hw_loc_label: "Location",
    hw_loc_val: "Singapore Datacenter · APAC low-latency hub",
    hw_cpu_label: "Processor",
    hw_cpu_val: "18-Core AMD EPYC",
    hw_ram_label: "Memory",
    hw_ram_val: "96 GB High-Speed ECC RAM",
    hw_disk_label: "Storage",
    hw_disk_val: "1.8 TB NVMe · Enterprise Datacenter Grade",
    hw_os_label: "Operating System",
    hw_os_val: "Ubuntu 24.04 LTS",
    sw_title: "Core Software Stack",
    sw_p2p_label: "P2P Port",
    sw_p2p_val: "30333 · TCP / WS · Open",
    sw_rpc_label: "RPC Endpoint",
    sw_rpc_val:
      "Secured Localhost — public WSS / HTTPS via rpc.micells.io pending",
    sec3_tag: "03 · Connection Guide",
    sec3_title_a: "Add the relay as",
    sec3_title_b: "a bootnode.",
    sec3_desc:
      "Choose the integration path that matches your setup. All snippets use the verified IPv4 multiaddr.",
    firewall_title: "Port & Firewall Reminder",
    firewall_desc:
      "Ensure outgoing TCP on port 30333 is unblocked so your node can establish the WebSocket connection with MiCells-Midnight-Relay. RPC port 9944 is intentionally bound to localhost on our server for security; public HTTPS / WSS access will be routed through rpc.micells.io.",
    sec4_tag: "04 · Network Reach",
    sec4_title_a: "Live peering topology",
    sec4_title_b: "from Singapore.",
    sec4_desc:
      "Dynamic P2P gossip mesh visualisation mapping active connections into and out of the Singapore hub node.",
    map_disclaimer:
      "Live node metrics and peer locations are refreshed via JSON-RPC endpoint (rpc.micells.io).",
    map_source_snapshot: "Operator snapshot · updated {ts}",
    map_source_live: "Live · fetched via rpc.micells.io",
    map_legend_home: "Home",
    map_legend_peer: "Peer",
    sec5_tag: "05 · Knowledge Base",
    sec5_title_a: "Frequently asked",
    sec5_title_b: "questions.",
    faq1_q: "What is a Midnight relay node?",
    faq1_a:
      "A public full node that routes transactions, blocks and zero-knowledge proof state transitions across the peer-to-peer network without authoring blocks itself.",
    faq2_q: "Why do public relays matter?",
    faq2_a:
      "They shield block producers from denial-of-service attacks, ensure rapid global gossip propagation, and allow lightweight clients or new nodes to synchronise quickly.",
    faq3_q: "What hardware do we recommend?",
    faq3_a:
      "Minimum: 8-core CPU, 32 GB RAM, NVMe storage and 1 Gbps unmetered bandwidth. MiCells® over-provisions (18 cores, 96 GB RAM) for maximum uptime and headroom.",
    faq4_q: "How can someone verify the node is online?",
    faq4_a:
      "Test the TCP/WS socket with `nc -zv 194.233.76.164 30333`, or inspect active peer routing in your node's info metrics after adding the multiaddr as a bootnode.",
    faq5_q: "What is the difference between a relay node and a block producer?",
    faq5_a:
      "Block producers hold private signing keys and forge blocks. Relay nodes hold no signing keys and act as defensive shields plus network propagators — routing data between producers and the wider network.",
    faq6_q: "How does Midnight relate to Cardano?",
    faq6_a:
      "Midnight is a privacy-focused partner chain built within the Cardano ecosystem. It leverages Cardano's security model while adding programmable privacy through ZK-SNARKs and Compact smart contracts.",
    sec6_tag: "06 · Peer With Us",
    sec6_title_a: "Building on Midnight?",
    sec6_title_b: "Peer with MiCells®.",
    sec6_desc:
      "Add the multiaddr as a bootnode, or reach out to our infrastructure team for a private peering conversation. We respond within one business day.",
    cta_docs: "Midnight Documentation",
    cta_contact: "Contact Infrastructure Team",
    home_hub_label: "MiCells Relay",
    home_hub_sub: "Home node",
  },
};

const zh = {
  nav: {
    framework: "框架",
    process: "流程",
    advisory: "顧問委員會",
    insights: "觀點",
    register: "表達意向",
    infrastructure: "基礎設施",
    partners: "合作夥伴",
    requestInfo: "索取資料",
    toggleAria: "切換語言",
  },
  hero: {
    line1: "生物保障",
    line2Italic: "源於",
    line3: "自體血液儲存。",
    subheadline:
      "MiCells® 總部設於香港,致力打造長期個人血液儲存的基礎設施,讓個人得以保存、監察並持續掌握自身血液,以備未來醫療所需。",
    primaryCta: "表達意向",
    secondaryCta: "了解框架",
  },
  pillars: {
    eyebrow: "01 · 核心理念",
    headingA: "支撐長期個人血液儲存的",
    headingB: "四項核心理念。",
    items: [
      {
        title: "醫療自主",
        body: "您的血液。您的生命。以自體專屬協議妥善儲存。",
      },
      {
        title: "臨床誠信",
        body: "與資深醫療專業人員共同發展,並受獨立審查的嚴謹標準所監督。",
      },
      {
        title: "安全與可追溯",
        body: "完整的監管鏈及資產核實貫穿整個保存週期。",
      },
      {
        title: "全球應變準備",
        body: "支援國際流動及跨司法管轄區的緊急應變需求。",
      },
    ],
  },
  clients: {
    eyebrow: "02 · 服務對象",
    headingA: "為超越傳統醫療規劃",
    headingB: "而作準備的人士而設。",
    body:
      "MiCells® 透過受臨床監管的個人血液儲存服務,支持追求長遠生物準備的個人及家庭。",
    list: [
      "追求長遠準備的個人",
      "高淨值人士",
      "罕見血型人士",
      "家族辦公室與傳承規劃",
      "國際醫療客戶",
    ],
  },
  process: {
    eyebrow: "03 · 流程",
    headingA: "五個階段的",
    headingB: "託管流程。",
    steps: [
      {
        title: "醫療諮詢",
        body: "初步臨床評估,以判定適合度、個別考量及量身訂造的儲存方案。",
      },
      {
        title: "採集與核實",
        body:
          "由獲認證的臨床合作夥伴進行受控生物採集,繼而進行身份、完整性及來源核實。",
      },
      {
        title: "安全保存",
        body: "於先進冷凍協議及持續監察下,轉移至機構級保存基礎設施。",
      },
      {
        title: "持續管理",
        body: "週期完整性管理、定期審查、備援儲存架構及機密客戶報告。",
      },
      {
        title: "有需要時的調動",
        body: "於完整書面監管鏈紀錄下,協調釋出至治療機構。",
      },
    ],
  },
  framework: {
    eyebrow: "04 · 框架",
    headingA: "建基於",
    headingB: "臨床監督之上。",
    body:
      "MiCells® 的框架設計旨在於臨床醫生、審計人員及監管機構面前皆可經得起考驗。",
    items: [
      {
        title: "醫學顧問委員會",
        body: "資深醫療專業人員就臨床安全、適合度協議及不斷演進的保存科學提供指導。",
      },
      {
        title: "合規理念",
        body: "主動的監管姿態,前瞻機構、跨境及新興的合規框架。",
      },
      {
        title: "臨床管治",
        body: "對影響生物完整性、客戶安全及監管鏈的決策實施獨立管治。",
      },
      {
        title: "品質保證",
        body: "覆蓋採集、運輸、儲存及調動的完整品質協議,全流程可審計。",
      },
    ],
  },
  advisory: {
    eyebrow: "05 · 顧問委員會",
    headingA: "顧問",
    headingB: "委員會。",
    ctaLabel: "資深醫學或科學顧問?",
    ctaHeading: "我們歡迎機密的顧問討論。",
    ctaButton: "洽談顧問席位",
  },
  insights: {
    eyebrow: "06 · 觀點",
    headingA: "研究與",
    headingB: "觀點。",
    read: "閱讀觀點",
  },
  register: {
    eyebrow: "07 · 表達意向",
    headingA: "表達您的",
    headingB: "MiCells® 意向。",
    intro:
      "為 MiCells® 自體血液儲存計劃之潛在客戶而設的保密、不具約束力之登記渠道。您的提交將協助我們規劃容量、優化服務設計,並就香港及國際市場之洽談安排排序。",
    assurances: [
      {
        title: "不具約束力",
        body: "不構成任何合約,雙方均無義務。",
      },
      {
        title: "嚴格保密",
        body: "絕不出售或與第三方分享,可隨時要求撤回。",
      },
      {
        title: "僅供需求預測",
        body: "僅用於推算潛在客戶數目及優化服務設計。",
      },
    ],
    groups: {
      about: "01 · 個人資料",
      profile: "02 · 個人背景",
      interest: "03 · 意向與時程",
      optional: "04 · 選填項目",
    },
    fields: {
      name: "姓名",
      email: "電郵",
      phone: "電話(含國碼)",
      phoneHint: "選填",
      country: "居住國家 / 地區",
      age: "年齡層",
      bloodType: "血型",
      household: "家庭覆蓋範圍",
      householdCount: "人數",
      motivation: "主要考量",
      timeline: "偏好洽談時程",
      tier: "偏好服務級別",
      referral: "得知本公司途徑",
      referralHint: "選填",
      notes: "其他補充事項",
      notesPlaceholder: "如有任何背景、疑問或偏好,請於此說明。",
    },
    options: {
      age: [
        { v: "18-30", l: "18 – 30 歲" },
        { v: "31-45", l: "31 – 45 歲" },
        { v: "46-60", l: "46 – 60 歲" },
        { v: "61-75", l: "61 – 75 歲" },
        { v: "76+", l: "76 歲以上" },
        { v: "prefer_not", l: "不願透露" },
      ],
      bloodType: [
        { v: "unknown", l: "未知" },
        { v: "A+", l: "A+" },
        { v: "A-", l: "A−" },
        { v: "B+", l: "B+" },
        { v: "B-", l: "B−" },
        { v: "AB+", l: "AB+" },
        { v: "AB-", l: "AB−" },
        { v: "O+", l: "O+" },
        { v: "O-", l: "O−" },
        { v: "rare", l: "罕見 / 其他" },
      ],
      household: [
        { v: "self", l: "本人" },
        { v: "self_partner", l: "本人及伴侶" },
        { v: "family", l: "整個家庭" },
        { v: "extended", l: "家族 / 家族辦公室" },
      ],
      motivation: [
        { v: "preparedness", l: "個人醫療準備" },
        { v: "rare_blood", l: "罕見血型" },
        { v: "family_history", l: "家族病史" },
        { v: "longevity", l: "長壽規劃" },
        { v: "executive", l: "高階或高流動性生活方式" },
        { v: "other", l: "其他" },
      ],
      timeline: [
        { v: "immediate", l: "即時(0 – 3 個月)" },
        { v: "3-6", l: "3 – 6 個月" },
        { v: "6-12", l: "6 – 12 個月" },
        { v: "exploratory", l: "純粹探索" },
      ],
      tier: [
        { v: "standard", l: "標準" },
        { v: "priority", l: "優先" },
        { v: "family", l: "家庭" },
        { v: "undecided", l: "尚未決定" },
      ],
    },
    terms: {
      eyebrow: "不具約束力之條款",
      body:
        "提交即表示閣下明白此意向表達並不構成合約,亦不對閣下與 MiCells® 產生任何法律或財務義務。所提供資料將僅用於預測潛在客戶數目、優化服務設計及規劃容量。所有提交均以機密方式處理,絕不出售或與第三方分享,並可隨時透過電郵至 info@micells.io 撤回。",
      consent:
        "本人明白此為不具法律約束力之意向表達,並同意 MiCells® 為需求預測及服務設計目的保存本表所載資料。",
    },
    submit: "提交意向登記",
    submitting: "傳送中…",
    validationRequired: "請填寫姓名及電郵以繼續。",
    validationConsent: "請勾選同意項以提交。",
    successTitle: "感謝您。",
    successBody:
      "您的意向登記已收悉。我們將以機密方式處理,並僅用於需求預測及服務設計。如日後有相關資訊,MiCells® 團隊將以下列電郵與閣下聯絡:",
    successBodyTail: "。",
    reset: "提交另一項意向登記",
    share: {
      eyebrow: "推薦予同儕",
      body:
        "認識可能重視私人醫療管理的親友嗎?一次私下轉發,勝過任何公開分享。",
      copyLink: "複製連結",
      copyLinkOk: "連結已複製至剪貼板",
      email: "電郵",
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      emailSubject: "MiCells® — 私人醫療管理",
      emailBody:
        "或許您會感興趣 — 一個以臨床、機構級態度處理個人醫療準備的方案。\n\nhttps://micells.io",
      shareText:
        "或許您會感興趣 — 一個以臨床、機構級態度處理個人醫療準備的方案。https://micells.io",
      followLinkedin: "於 LinkedIn 關注 MiCells®",
      close: "關閉",
    },
    toastSuccess: "意向登記已收悉。",
    toastFail: "無法提交。請重試或直接電郵至 info@micells.io。",
    captcha: {
      eyebrow: "人機驗證",
      headingA: "為",
      headingB: "MiCells® 流程排序。",
      body:
        "請依 MiCells® 計劃的正確順序,點選每個步驟。此步驟旨在確認閣下並非自動化程式,再提交您的意向登記。",
      steps: ["諮詢", "採集", "保存", "守護"],
      steps_label: "步驟",
      yourOrder: "您的順序",
      emptySlot: "—",
      confirm: "確認順序",
      reset: "重設",
      cancel: "取消",
      wrong: "順序不正確 — 請再試一次。",
      attempts: "第 {n} 次嘗試",
    },
  },
  investors: {
    eyebrow: "08 · 合作夥伴",
    headingA: "合作夥伴與",
    headingB: "投資者。",
    body:
      "MiCells® 是一間總部設於香港的醫療基礎設施公司,於保存與長期醫療連續性之交匯處運作。合作透過引薦及合資格查詢進行。",
    tracks: [
      {
        label: "策略性合作",
        body:
          "支持長期個人血液儲存基礎設施建設的臨床、保存、物流及醫療合作夥伴。",
      },
      {
        label: "醫學與策略顧問",
        body: "支援臨床管治、監管發展及長遠醫療部署。",
      },
      {
        label: "投資者查詢",
        body:
          "來自家族辦公室、機構基金及具長期視野資本的合資格投資者查詢。",
      },
    ],
    materialsLabel: "投資者資料",
    materialsBody: "投資者資料按合資格方要求提供。",
    cta: "索取投資者資料",
    trackPrefix: "軌道",
  },
  infrastructureTeaser: {
    eyebrow: "09 · 基礎設施",
    headingA: "為 Midnight Network 而設的",
    headingB: "公共基礎設施。",
    body:
      "MiCells® 於新加坡營運高可用性公共中繼節點,加強亞太區的網絡傳輸與穩定性,支援保護私隱的運算。實時節點狀態、對等身份及連線參數均公開透明。",
    stats: {
      status: "節點狀態",
      statusValue: "持續監察",
      region: "地區",
      regionValue: "新加坡 · 亞太區",
      network: "網絡",
      networkValue: "Midnight · 主網中繼",
    },
    cta: "探索節點",
    ctaAria: "開啟 MiCells® Midnight 中繼基礎設施頁面",
  },
  contact: {
    eyebrow: "10 · 聯絡",
    headingA: "聯絡",
    headingB: "MiCells®。",
    intro:
      "查詢均以機密方式處理。MiCells® 團隊將以下列電郵回覆閣下:",
    directLabel: "直接聯絡",
    locationLabel: "地點",
    locationValue: "香港特別行政區",
    fields: {
      name: "姓名",
      organisation: "機構",
      email: "電郵",
      country: "國家",
      enquiryType: "查詢類型",
      message: "訊息",
      messagePlaceholder: "請簡要描述查詢性質。",
    },
    types: {
      general: "一般查詢",
      investor: "投資者資料",
      information: "服務資料",
      advisory: "顧問 / 合作",
    },
    disclaimer:
      "提交即表示閣下同意 MiCells® 就此查詢與您聯絡。我們不會與第三方分享查詢者資料。",
    submit: "發送查詢",
    submitting: "傳送中…",
    successTitle: "查詢已收悉。",
    successBody:
      "感謝您聯絡 MiCells®。我們的團隊將以下列電郵回覆:",
    successBodyTail: "。如查詢具時效性,請直接透過電郵聯絡我們。",
    reset: "提交另一項查詢",
    validationRequired: "請填寫姓名、電郵及訊息。",
    toastSuccess:
      "查詢已收悉。我們的團隊將以 info@micells.io 回覆閣下。",
    toastFail:
      "無法提交查詢。請重試或直接電郵至 info@micells.io。",
  },
  footer: {
    description: "醫療基礎設施,讓個人於有需要時保存並取用自身血液。",
    address: [
      "新界沙田火炭",
      "坳背灣街 37–39 號",
      "豐達中心 12 樓 14A 室",
    ],
    location: "香港特別行政區 · 亞洲",
    contact: "聯絡",
    connect: "社交媒體",
    legal: "法律",
    linkedin: "領英",
    x: "X / Twitter",
    infrastructure: "基礎設施",
    midnightRelay: "Midnight Relay",
    privacy: "私隱政策",
    terms: "使用條款",
    copyright: "© {year} MiCells®。版權所有。",
  },
  article: {
    close: "關閉",
    published:
      "由 MiCells® 發表。如就所述任何主題有查詢,歡迎與我們聯絡。",
    cta: "聯絡 MiCells®",
    minutesSuffix: "分鐘",
  },
  relay: {
    badge_syncing: "節點在線 · 同步中",
    badge_synced: "節點在線 · 完全同步",
    badge_last_verified: "已驗證運維快照",
    eyebrow: "基礎設施 · 公共節點",
    hero_title_a: "為 Midnight 提供",
    hero_title_b: "隱私保護",
    hero_title_c: "基礎設施。",
    hero_desc:
      "MiCells® 於新加坡運營高可用、高吞吐量的 Midnight 公共中繼節點,支援 Midnight 網絡生態發展。歡迎與我們建立對等連接,共同增強亞太地區的節點傳播效率與網絡韌性。",
    peer_count_label: "活躍對等節點",
    peer_count_suffix: "已連接",
    sync_progress_label: "同步進度",
    uptime_label: "節點狀態",
    uptime_value: "已監控",
    primary_multiaddr_label: "主要 Multiaddr · IPv4",
    ws_tcp: "WS · TCP",
    peer_id_label: "Peer ID",
    copy_multiaddr: "點擊複製 Multiaddr",
    copy_multiaddr_short: "複製 Multiaddr",
    copy_peer_id: "複製 Peer ID",
    copy_short: "複製",
    copied: "已複製",
    copied_toast: "已複製至剪貼板",
    copy_fail_toast: "複製失敗 — 請手動複製。",
    peer_with_us: "與 MiCells® 對等",
    sec1_tag: "01 · 戰略願景",
    sec1_title_a: "為何 MiCells®",
    sec1_title_b: "運營此節點。",
    sec1_desc:
      "隱私保護運算層是機構級醫療、金融及身分工作負載的先決條件。MiCells® 貢獻公共中繼容量,以此作為對網絡長期治理與維護的承諾。",
    card1_title: "機密執行層",
    card1_desc:
      "MiCells® 將 Midnight 用作零知識執行環境:提供密碼學可驗證的運算能力,同時不犧牲數據隱私或去中心化特性。",
    card2_title: "ZK-SNARK 合規與保護",
    card2_desc:
      "零知識證明允許具備選擇性披露的私密運算 — 這一關鍵原語使隱私保護基礎設施得以在機構、審計人員及監管機構面前站得住腳。",
    card3_title: "公共基礎設施承諾",
    card3_desc:
      "MiCells® 承諾於亞太地區維持免費、低延遲的公共中繼容量,強化對等節點傳播,保障 Midnight 生態的長期網絡韌性。",
    sec2_tag: "02 · 硬件與軟件",
    sec2_title_a: "企業級就緒",
    sec2_title_b: "架構。",
    sec2_desc:
      "超規格數據中心硬件,配以經過強化及審計的軟件堆疊 — 這是 MiCells® 願意以自身品牌運行的基礎設施最低標準。",
    hw_title: "伺服器規格",
    hw_loc_label: "位置",
    hw_loc_val: "新加坡數據中心 · 亞太低延遲樞紐",
    hw_cpu_label: "處理器",
    hw_cpu_val: "18 核 AMD EPYC",
    hw_ram_label: "記憶體",
    hw_ram_val: "96 GB 高速 ECC RAM",
    hw_disk_label: "儲存",
    hw_disk_val: "1.8 TB NVMe · 企業級數據中心規格",
    hw_os_label: "作業系統",
    hw_os_val: "Ubuntu 24.04 LTS",
    sw_title: "核心軟件堆疊",
    sw_p2p_label: "P2P 端口",
    sw_p2p_val: "30333 · TCP / WS · 公開",
    sw_rpc_label: "RPC 端點",
    sw_rpc_val:
      "本地隔離安全 — 公共 WSS / HTTPS 將透過 rpc.micells.io 提供",
    sec3_tag: "03 · 連接指南",
    sec3_title_a: "將中繼加入為",
    sec3_title_b: "Bootnode。",
    sec3_desc:
      "選擇適合您配置的整合方式。所有代碼片段均使用經驗證的 IPv4 multiaddr。",
    firewall_title: "端口與防火牆提示",
    firewall_desc:
      "請確保出站 TCP 端口 30333 未被封鎖,以便您的節點與 MiCells-Midnight-Relay 建立 WebSocket 連接。基於安全考量,RPC 端口 9944 目前綁定於伺服器本地;公共 HTTPS / WSS 存取將透過 rpc.micells.io 統一提供。",
    sec4_tag: "04 · 網絡覆蓋",
    sec4_title_a: "來自新加坡的",
    sec4_title_b: "實時對等拓撲。",
    sec4_desc:
      "動態 P2P Gossip 網絡可視化 — 展示新加坡樞紐節點的進出連接。",
    map_disclaimer:
      "實時節點指標及對等節點位置透過 JSON-RPC 端點(rpc.micells.io)動態更新。",
    map_source_snapshot: "運維快照 · 更新於 {ts}",
    map_source_live: "實時 · 由 rpc.micells.io 取得",
    map_legend_home: "本節點",
    map_legend_peer: "對等",
    sec5_tag: "05 · 知識庫",
    sec5_title_a: "常見",
    sec5_title_b: "問題。",
    faq1_q: "什麼是 Midnight 中繼節點?",
    faq1_a:
      "公共全節點,在對等網絡中路由交易、區塊及零知識證明狀態轉換,本身並不參與出塊。",
    faq2_q: "為何公共中繼節點至關重要?",
    faq2_a:
      "它們保護出塊節點免受拒絕服務攻擊、確保全球 gossip 快速傳播,並讓輕量客戶端或新節點快速同步。",
    faq3_q: "推薦的硬件配置為何?",
    faq3_a:
      "最低配置:8 核 CPU、32 GB 記憶體、NVMe 儲存及 1 Gbps 無限流量頻寬。MiCells® 採用超規格架構(18 核、96 GB 記憶體)以確保最高可用性與效能冗餘。",
    faq4_q: "如何驗證節點是否在線?",
    faq4_a:
      "使用 `nc -zv 194.233.76.164 30333` 測試 TCP/WS 通訊端;或將 multiaddr 加入為 bootnode 後,檢視您節點 info 指標中的對等節點路由。",
    faq5_q: "中繼節點與出塊節點有何分別?",
    faq5_a:
      "出塊節點持有私密簽章密鑰,負責產生區塊。中繼節點不持出塊密鑰,擔當防禦屏障及網絡傳播者 — 在出塊節點與更廣網絡之間路由數據。",
    faq6_q: "Midnight 與 Cardano 的關係?",
    faq6_a:
      "Midnight 為 Cardano 生態內著重隱私的合作鏈。它繼承了 Cardano 的安全模型,並透過 ZK-SNARKs 及 Compact 智能合約新增可編程隱私功能。",
    sec6_tag: "06 · 與我們對等",
    sec6_title_a: "正在構建 Midnight?",
    sec6_title_b: "與 MiCells® 對等。",
    sec6_desc:
      "將 multiaddr 加入為 bootnode,或聯絡我們的基礎設施團隊討論私密對等連接。我們會於一個工作日內回覆。",
    cta_docs: "Midnight 官方文檔",
    cta_contact: "聯絡基礎設施團隊",
    home_hub_label: "MiCells 中繼",
    home_hub_sub: "本節點",
  },
};

const zhCn = {
  nav: {
    framework: "框架",
    process: "流程",
    advisory: "顾问委员会",
    insights: "观点",
    register: "表达意向",
    infrastructure: "基础设施",
    partners: "合作伙伴",
    requestInfo: "索取资料",
    toggleAria: "切换语言",
  },
  hero: {
    line1: "生物保障",
    line2Italic: "源于",
    line3: "自体血液储存。",
    subheadline:
      "MiCells® 总部设于香港,致力打造长期个人血液储存的基础设施,让个人得以保存、监察并持续掌握自身血液,以备未来医疗所需。",
    primaryCta: "表达意向",
    secondaryCta: "了解框架",
  },
  pillars: {
    eyebrow: "01 · 核心理念",
    headingA: "支撑长期个人血液储存的",
    headingB: "四项核心理念。",
    items: [
      {
        title: "医疗自主",
        body: "您的血液。您的生命。以自体专属协议妥善储存。",
      },
      {
        title: "临床诚信",
        body: "与资深医疗专业人员共同发展,并受独立审查的严谨标准所监督。",
      },
      {
        title: "安全与可追溯",
        body: "完整的监管链及资产核实贯穿整个保存周期。",
      },
      {
        title: "全球应变准备",
        body: "支援国际流动及跨司法管辖区的紧急应变需求。",
      },
    ],
  },
  clients: {
    eyebrow: "02 · 服务对象",
    headingA: "为超越传统医疗规划",
    headingB: "而作准备的人士而设。",
    body:
      "MiCells® 透过受临床监管的个人血液储存服务,支持追求长远生物准备的个人及家庭。",
    list: [
      "追求长远准备的个人",
      "高净值人士",
      "罕见血型人士",
      "家族办公室与传承规划",
      "国际医疗客户",
    ],
  },
  process: {
    eyebrow: "03 · 流程",
    headingA: "五个阶段的",
    headingB: "托管流程。",
    steps: [
      {
        title: "医疗咨询",
        body: "初步临床评估,以判定适合度、个别考量及量身订造的储存方案。",
      },
      {
        title: "采集与核实",
        body:
          "由获认证的临床合作伙伴进行受控生物采集,继而进行身份、完整性及来源核实。",
      },
      {
        title: "安全保存",
        body: "于先进冷冻协议及持续监察下,转移至机构级保存基础设施。",
      },
      {
        title: "持续管理",
        body: "周期完整性管理、定期审查、备援储存架构及机密客户报告。",
      },
      {
        title: "有需要时的调动",
        body: "于完整书面监管链纪录下,协调释出至治疗机构。",
      },
    ],
  },
  framework: {
    eyebrow: "04 · 框架",
    headingA: "建基于",
    headingB: "临床监督之上。",
    body:
      "MiCells® 的框架设计旨在于临床医生、审计人员及监管机构面前皆可经得起考验。",
    items: [
      {
        title: "医学顾问委员会",
        body: "资深医疗专业人员就临床安全、适合度协议及不断演进的保存科学提供指导。",
      },
      {
        title: "合规理念",
        body: "主动的监管姿态,前瞻机构、跨境及新兴的合规框架。",
      },
      {
        title: "临床管治",
        body: "对影响生物完整性、客户安全及监管链的决策实施独立管治。",
      },
      {
        title: "品质保证",
        body: "覆盖采集、运输、储存及调动的完整品质协议,全流程可审计。",
      },
    ],
  },
  advisory: {
    eyebrow: "05 · 顾问委员会",
    headingA: "顾问",
    headingB: "委员会。",
    ctaLabel: "资深医学或科学顾问?",
    ctaHeading: "我们欢迎机密的顾问讨论。",
    ctaButton: "洽谈顾问席位",
  },
  insights: {
    eyebrow: "06 · 观点",
    headingA: "研究与",
    headingB: "观点。",
    read: "阅读观点",
  },
  register: {
    eyebrow: "07 · 表达意向",
    headingA: "表达您的",
    headingB: "MiCells® 意向。",
    intro:
      "为 MiCells® 自体血液储存计划之潜在客户而设的保密、不具约束力之登记渠道。您的提交将协助我们规划容量、优化服务设计,并就香港及国际市场之洽谈安排排序。",
    assurances: [
      {
        title: "不具约束力",
        body: "不构成任何合约,双方均无义务。",
      },
      {
        title: "严格保密",
        body: "绝不出售或与第三方分享,可随时要求撤回。",
      },
      {
        title: "仅供需求预测",
        body: "仅用于推算潜在客户数目及优化服务设计。",
      },
    ],
    groups: {
      about: "01 · 个人资料",
      profile: "02 · 个人背景",
      interest: "03 · 意向与时程",
      optional: "04 · 选填项目",
    },
    fields: {
      name: "姓名",
      email: "电邮",
      phone: "电话(含国码)",
      phoneHint: "选填",
      country: "居住国家 / 地区",
      age: "年龄层",
      bloodType: "血型",
      household: "家庭覆盖范围",
      householdCount: "人数",
      motivation: "主要考量",
      timeline: "偏好洽谈时程",
      tier: "偏好服务级别",
      referral: "得知本公司途径",
      referralHint: "选填",
      notes: "其他补充事项",
      notesPlaceholder: "如有任何背景、疑问或偏好,请于此说明。",
    },
    options: {
      age: [
        { v: "18-30", l: "18 – 30 岁" },
        { v: "31-45", l: "31 – 45 岁" },
        { v: "46-60", l: "46 – 60 岁" },
        { v: "61-75", l: "61 – 75 岁" },
        { v: "76+", l: "76 岁以上" },
        { v: "prefer_not", l: "不愿透露" },
      ],
      bloodType: [
        { v: "unknown", l: "未知" },
        { v: "A+", l: "A+" },
        { v: "A-", l: "A−" },
        { v: "B+", l: "B+" },
        { v: "B-", l: "B−" },
        { v: "AB+", l: "AB+" },
        { v: "AB-", l: "AB−" },
        { v: "O+", l: "O+" },
        { v: "O-", l: "O−" },
        { v: "rare", l: "罕见 / 其他" },
      ],
      household: [
        { v: "self", l: "本人" },
        { v: "self_partner", l: "本人及伴侣" },
        { v: "family", l: "整个家庭" },
        { v: "extended", l: "家族 / 家族办公室" },
      ],
      motivation: [
        { v: "preparedness", l: "个人医疗准备" },
        { v: "rare_blood", l: "罕见血型" },
        { v: "family_history", l: "家族病史" },
        { v: "longevity", l: "长寿规划" },
        { v: "executive", l: "高阶或高流动性生活方式" },
        { v: "other", l: "其他" },
      ],
      timeline: [
        { v: "immediate", l: "即时(0 – 3 个月)" },
        { v: "3-6", l: "3 – 6 个月" },
        { v: "6-12", l: "6 – 12 个月" },
        { v: "exploratory", l: "纯粹探索" },
      ],
      tier: [
        { v: "standard", l: "标准" },
        { v: "priority", l: "优先" },
        { v: "family", l: "家庭" },
        { v: "undecided", l: "尚未决定" },
      ],
    },
    terms: {
      eyebrow: "不具约束力之条款",
      body:
        "提交即表示阁下明白此意向表达并不构成合约,亦不对阁下与 MiCells® 产生任何法律或财务义务。所提供资料将仅用于预测潜在客户数目、优化服务设计及规划容量。所有提交均以机密方式处理,绝不出售或与第三方分享,并可随时透过电邮至 info@micells.io 撤回。",
      consent:
        "本人明白此为不具法律约束力之意向表达,并同意 MiCells® 为需求预测及服务设计目的保存本表所载资料。",
    },
    submit: "提交意向登记",
    submitting: "传送中…",
    validationRequired: "请填写姓名及电邮以继续。",
    validationConsent: "请勾选同意项以提交。",
    successTitle: "感谢您。",
    successBody:
      "您的意向登记已收悉。我们将以机密方式处理,并仅用于需求预测及服务设计。如日后有相关资讯,MiCells® 团队将以下列电邮与阁下联络:",
    successBodyTail: "。",
    reset: "提交另一项意向登记",
    share: {
      eyebrow: "推荐予同侪",
      body:
        "认识可能重视私人医疗管理的亲友吗?一次私下转发,胜过任何公开分享。",
      copyLink: "复制连结",
      copyLinkOk: "连结已复制至剪贴板",
      email: "电邮",
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      emailSubject: "MiCells® — 私人医疗管理",
      emailBody:
        "或许您会感兴趣 — 一个以临床、机构级态度处理个人医疗准备的方案。\n\nhttps://micells.io",
      shareText:
        "或许您会感兴趣 — 一个以临床、机构级态度处理个人医疗准备的方案。https://micells.io",
      followLinkedin: "于 LinkedIn 关注 MiCells®",
      close: "关闭",
    },
    toastSuccess: "意向登记已收悉。",
    toastFail: "无法提交。请重试或直接电邮至 info@micells.io。",
    captcha: {
      eyebrow: "人机验证",
      headingA: "为",
      headingB: "MiCells® 流程排序。",
      body:
        "请依 MiCells® 计划的正确顺序,点选每个步骤。此步骤旨在确认阁下并非自动化程式,再提交您的意向登记。",
      steps: ["咨询", "采集", "保存", "守护"],
      steps_label: "步骤",
      yourOrder: "您的顺序",
      emptySlot: "—",
      confirm: "确认顺序",
      reset: "重设",
      cancel: "取消",
      wrong: "顺序不正确 — 请再试一次。",
      attempts: "第 {n} 次尝试",
    },
  },
  investors: {
    eyebrow: "08 · 合作伙伴",
    headingA: "合作伙伴与",
    headingB: "投资者。",
    body:
      "MiCells® 是一间总部设于香港的医疗基础设施公司,于保存与长期医疗连续性之交汇处运作。合作透过引荐及合资格查询进行。",
    tracks: [
      {
        label: "策略性合作",
        body:
          "支持长期个人血液储存基础设施建设的临床、保存、物流及医疗合作伙伴。",
      },
      {
        label: "医学与策略顾问",
        body: "支援临床管治、监管发展及长远医疗部署。",
      },
      {
        label: "投资者查询",
        body:
          "来自家族办公室、机构基金及具长期视野资本的合资格投资者查询。",
      },
    ],
    materialsLabel: "投资者资料",
    materialsBody: "投资者资料按合资格方要求提供。",
    cta: "索取投资者资料",
    trackPrefix: "轨道",
  },
  infrastructureTeaser: {
    eyebrow: "09 · 基础设施",
    headingA: "为 Midnight Network 而设的",
    headingB: "公共基础设施。",
    body:
      "MiCells® 于新加坡运营高可用性公共中继节点,加强亚太区的网络传输与稳定性,支持保护隐私的运算。实时节点状态、对等身份及连线参数均公开透明。",
    stats: {
      status: "节点状态",
      statusValue: "持续监察",
      region: "地区",
      regionValue: "新加坡 · 亚太区",
      network: "网络",
      networkValue: "Midnight · 主网中继",
    },
    cta: "探索节点",
    ctaAria: "打开 MiCells® Midnight 中继基础设施页面",
  },
  contact: {
    eyebrow: "10 · 联络",
    headingA: "联络",
    headingB: "MiCells®。",
    intro:
      "查询均以机密方式处理。MiCells® 团队将以下列电邮回复阁下:",
    directLabel: "直接联络",
    locationLabel: "地点",
    locationValue: "香港特别行政区",
    fields: {
      name: "姓名",
      organisation: "机构",
      email: "电邮",
      country: "国家",
      enquiryType: "查询类型",
      message: "讯息",
      messagePlaceholder: "请简要描述查询性质。",
    },
    types: {
      general: "一般查询",
      investor: "投资者资料",
      information: "服务资料",
      advisory: "顾问 / 合作",
    },
    disclaimer:
      "提交即表示阁下同意 MiCells® 就此查询与您联络。我们不会与第三方分享查询者资料。",
    submit: "发送查询",
    submitting: "传送中…",
    successTitle: "查询已收悉。",
    successBody:
      "感谢您联络 MiCells®。我们的团队将以下列电邮回复:",
    successBodyTail: "。如查询具时效性,请直接透过电邮联络我们。",
    reset: "提交另一项查询",
    validationRequired: "请填写姓名、电邮及讯息。",
    toastSuccess:
      "查询已收悉。我们的团队将以 info@micells.io 回复阁下。",
    toastFail:
      "无法提交查询。请重试或直接电邮至 info@micells.io。",
  },
  footer: {
    description: "医疗基础设施,让个人于有需要时保存并取用自身血液。",
    address: [
      "新界沙田火炭",
      "坳背湾街 37–39 号",
      "丰达中心 12 楼 14A 室",
    ],
    location: "香港特别行政区 · 亚洲",
    contact: "联络",
    connect: "社交媒体",
    legal: "法律",
    linkedin: "领英",
    x: "X / Twitter",
    infrastructure: "基础设施",
    midnightRelay: "Midnight Relay",
    privacy: "私隐政策",
    terms: "使用条款",
    copyright: "© {year} MiCells®。版权所有。",
  },
  article: {
    close: "关闭",
    published:
      "由 MiCells® 发表。如就所述任何主题有查询,欢迎与我们联络。",
    cta: "联络 MiCells®",
    minutesSuffix: "分钟",
  },
  relay: {
    badge_syncing: "节点在线 · 同步中",
    badge_synced: "节点在线 · 完全同步",
    badge_last_verified: "已验证运维快照",
    eyebrow: "基础设施 · 公共节点",
    hero_title_a: "为 Midnight 提供",
    hero_title_b: "隐私保护",
    hero_title_c: "基础设施。",
    hero_desc:
      "MiCells® 于新加坡运营高可用、高吞吐量的 Midnight 公共中继节点,支援 Midnight 网络生态发展。欢迎与我们建立对等连接,共同增强亚太地区的节点传播效率与网络韧性。",
    peer_count_label: "活跃对等节点",
    peer_count_suffix: "已连接",
    sync_progress_label: "同步进度",
    uptime_label: "节点状态",
    uptime_value: "已监控",
    primary_multiaddr_label: "主要 Multiaddr · IPv4",
    ws_tcp: "WS · TCP",
    peer_id_label: "Peer ID",
    copy_multiaddr: "点击复制 Multiaddr",
    copy_multiaddr_short: "复制 Multiaddr",
    copy_peer_id: "复制 Peer ID",
    copy_short: "复制",
    copied: "已复制",
    copied_toast: "已复制至剪贴板",
    copy_fail_toast: "复制失败 — 请手动复制。",
    peer_with_us: "与 MiCells® 对等",
    sec1_tag: "01 · 战略愿景",
    sec1_title_a: "为何 MiCells®",
    sec1_title_b: "运营此节点。",
    sec1_desc:
      "隐私保护运算层是机构级医疗、金融及身分工作负载的先决条件。MiCells® 贡献公共中继容量,以此作为对网络长期治理与维护的承诺。",
    card1_title: "机密执行层",
    card1_desc:
      "MiCells® 将 Midnight 用作零知识执行环境:提供密码学可验证的运算能力,同时不牺牲数据隐私或去中心化特性。",
    card2_title: "ZK-SNARK 合规与保护",
    card2_desc:
      "零知识证明允许具备选择性披露的私密运算 — 这一关键原语使隐私保护基础设施得以在机构、审计人员及监管机构面前站得住脚。",
    card3_title: "公共基础设施承诺",
    card3_desc:
      "MiCells® 承诺于亚太地区维持免费、低延迟的公共中继容量,强化对等节点传播,保障 Midnight 生态的长期网络韧性。",
    sec2_tag: "02 · 硬件与软件",
    sec2_title_a: "企业级就绪",
    sec2_title_b: "架构。",
    sec2_desc:
      "超规格数据中心硬件,配以经过强化及审计的软件堆叠 — 这是 MiCells® 愿意以自身品牌运行的基础设施最低标准。",
    hw_title: "伺服器规格",
    hw_loc_label: "位置",
    hw_loc_val: "新加坡数据中心 · 亚太低延迟枢纽",
    hw_cpu_label: "处理器",
    hw_cpu_val: "18 核 AMD EPYC",
    hw_ram_label: "记忆体",
    hw_ram_val: "96 GB 高速 ECC RAM",
    hw_disk_label: "储存",
    hw_disk_val: "1.8 TB NVMe · 企业级数据中心规格",
    hw_os_label: "作业系统",
    hw_os_val: "Ubuntu 24.04 LTS",
    sw_title: "核心软件堆叠",
    sw_p2p_label: "P2P 端口",
    sw_p2p_val: "30333 · TCP / WS · 公开",
    sw_rpc_label: "RPC 端点",
    sw_rpc_val:
      "本地隔离安全 — 公共 WSS / HTTPS 将透过 rpc.micells.io 提供",
    sec3_tag: "03 · 连接指南",
    sec3_title_a: "将中继加入为",
    sec3_title_b: "Bootnode。",
    sec3_desc:
      "选择适合您配置的整合方式。所有代码片段均使用经验证的 IPv4 multiaddr。",
    firewall_title: "端口与防火墙提示",
    firewall_desc:
      "请确保出站 TCP 端口 30333 未被封锁,以便您的节点与 MiCells-Midnight-Relay 建立 WebSocket 连接。基于安全考量,RPC 端口 9944 目前绑定于伺服器本地;公共 HTTPS / WSS 存取将透过 rpc.micells.io 统一提供。",
    sec4_tag: "04 · 网络覆盖",
    sec4_title_a: "来自新加坡的",
    sec4_title_b: "实时对等拓扑。",
    sec4_desc:
      "动态 P2P Gossip 网络可视化 — 展示新加坡枢纽节点的进出连接。",
    map_disclaimer:
      "实时节点指标及对等节点位置透过 JSON-RPC 端点(rpc.micells.io)动态更新。",
    map_source_snapshot: "运维快照 · 更新于 {ts}",
    map_source_live: "实时 · 由 rpc.micells.io 取得",
    map_legend_home: "本节点",
    map_legend_peer: "对等",
    sec5_tag: "05 · 知识库",
    sec5_title_a: "常见",
    sec5_title_b: "问题。",
    faq1_q: "什么是 Midnight 中继节点?",
    faq1_a:
      "公共全节点,在对等网络中路由交易、区块及零知识证明状态转换,本身并不参与出块。",
    faq2_q: "为何公共中继节点至关重要?",
    faq2_a:
      "它们保护出块节点免受拒绝服务攻击、确保全球 gossip 快速传播,并让轻量客户端或新节点快速同步。",
    faq3_q: "推荐的硬件配置为何?",
    faq3_a:
      "最低配置:8 核 CPU、32 GB 记忆体、NVMe 储存及 1 Gbps 无限流量频宽。MiCells® 采用超规格架构(18 核、96 GB 记忆体)以确保最高可用性与效能冗余。",
    faq4_q: "如何验证节点是否在线?",
    faq4_a:
      "使用 `nc -zv 194.233.76.164 30333` 测试 TCP/WS 通讯端;或将 multiaddr 加入为 bootnode 后,检视您节点 info 指标中的对等节点路由。",
    faq5_q: "中继节点与出块节点有何分别?",
    faq5_a:
      "出块节点持有私密签章密钥,负责产生区块。中继节点不持出块密钥,担当防御屏障及网络传播者 — 在出块节点与更广网络之间路由数据。",
    faq6_q: "Midnight 与 Cardano 的关系?",
    faq6_a:
      "Midnight 为 Cardano 生态内著重隐私的合作链。它继承了 Cardano 的安全模型,并透过 ZK-SNARKs 及 Compact 智能合约新增可编程隐私功能。",
    sec6_tag: "06 · 与我们对等",
    sec6_title_a: "正在构建 Midnight?",
    sec6_title_b: "与 MiCells® 对等。",
    sec6_desc:
      "将 multiaddr 加入为 bootnode,或联络我们的基础设施团队讨论私密对等连接。我们会于一个工作日内回复。",
    cta_docs: "Midnight 官方文档",
    cta_contact: "联络基础设施团队",
    home_hub_label: "MiCells 中继",
    home_hub_sub: "本节点",
  },
};

export const STRINGS = { en, zh, zhCn };
